import React, { useMemo } from "react";

import { useTheme } from "@mui/material";
import { useCustomerPortalJobContext } from "Context/CustomerPortalJobContext";
import { useSecretManager } from "Hooks/useSecretManager";
import { Gps as GpsIcon } from "iconsax-react";
import { useTranslation } from "react-i18next";
import env from "Util/env";

import { CustomerPortalCard } from "../../../../CustomerPortalCard";
import { CustomerPortalLoadingPortalSentences } from "../../../../CustomerPortalLoadingPortal";
import { BorderLinearProgress, BoxContainer, LoadingContainer } from "./styled";
import { SummaryLivePositionMap } from "./SummaryLivePositionMap";

type Props = {
  minHeight?: number;
};

export const SummaryLivePositionSection: React.FC<Props> = ({ minHeight }) => {
  const { t: translate } = useTranslation();
  const { customerShipmentsWithTracking, isLoading, isCustomerShipmentsLoading } =
    useCustomerPortalJobContext();
  const theme = useTheme();

  const { secret } = useSecretManager(env.SECRET_ID);

  const isInLoadingMode = useMemo(() => {
    return isLoading || isCustomerShipmentsLoading || !customerShipmentsWithTracking?.length;
  }, [isLoading, isCustomerShipmentsLoading, customerShipmentsWithTracking]);

  const googleMapsApiKey = useMemo(() => {
    return secret?.GOOGLE_MAPS_API_KEY;
  }, [secret]);

  const renderLoadingContainer = () => {
    if (!isInLoadingMode) return;

    return (
      <LoadingContainer>
        <BoxContainer>
          <BorderLinearProgress />
        </BoxContainer>

        <CustomerPortalLoadingPortalSentences />
      </LoadingContainer>
    );
  };

  const renderShipmentMap = () => {
    if (isInLoadingMode) return;

    if (!googleMapsApiKey) return;

    return (
      <SummaryLivePositionMap
        googleMapsApiKey={googleMapsApiKey}
        shipments={customerShipmentsWithTracking!}
        minHeight={minHeight}
      />
    );
  };

  return (
    <CustomerPortalCard
      title={
        isLoading ||
        isCustomerShipmentsLoading ||
        !customerShipmentsWithTracking.length ? undefined : (
          <>
            {translate("livePosition")}
            <GpsIcon size={24} color={theme.palette.primary.main} style={{ marginLeft: "8px" }} />
          </>
        )
      }
      block
      fullHeight
    >
      {renderLoadingContainer()}
      {renderShipmentMap()}
    </CustomerPortalCard>
  );
};
